import type { TooltipProps } from 'recharts';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useT } from '@kmgestao/i18n';
import { PageSectionCard, Skeleton } from '@kmgestao/ui';

import {
  formatCompactInteger,
  formatDashboardDay,
  formatWholeNumber,
  type AiKpisDailyPoint,
} from '../../../features/ai-analytics/index.js';

const chartColors = {
  platform: 'hsl(var(--chart-1))',
  byok: 'hsl(var(--chart-2))',
  errors: 'hsl(var(--destructive))',
};

export interface CallsChartProps {
  points: AiKpisDailyPoint[] | undefined;
  periodDays: number;
  loading?: boolean;
}

export function CallsChart({ points, periodDays, loading = false }: CallsChartProps) {
  const { t } = useT('dashboard');
  const hasUsage = (points ?? []).some((point) => point.callsTotal > 0);
  const latestPoint = [...(points ?? [])].reverse().find((point) => point.callsTotal > 0);

  return (
    <PageSectionCard
      title={t('chart.title')}
      description={t('chart.description', { days: periodDays })}
      contentClassName="space-y-4"
    >
      {!loading ? (
        <div className="flex flex-wrap gap-3">
          <LegendChip
            label={t('chart.legend.platform')}
            value={latestPoint ? formatWholeNumber(latestPoint.platformCalls) : '0'}
            color={chartColors.platform}
          />
          <LegendChip
            label={t('chart.legend.byok')}
            value={latestPoint ? formatWholeNumber(latestPoint.byokCalls) : '0'}
            color={chartColors.byok}
          />
          <LegendChip
            label={t('chart.legend.errors')}
            value={latestPoint ? formatWholeNumber(latestPoint.errorCalls) : '0'}
            color={chartColors.errors}
          />
        </div>
      ) : null}
      {loading ? <Skeleton className="h-[320px] w-full" /> : null}
      {!loading && !hasUsage ? (
        <div className="flex min-h-[300px] items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 px-6 text-center text-base text-muted-foreground sm:min-h-[320px]">
          {t('states.empty')}
        </div>
      ) : null}
      {!loading && hasUsage ? (
        <div className="h-[300px] w-full rounded-xl border border-border/70 bg-background p-3 sm:h-[360px] sm:p-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points ?? []} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                minTickGap={24}
                tickFormatter={formatDashboardDay}
                tick={{ fill: chartColors.platform, fontSize: 14 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={48}
                tickFormatter={formatCompactInteger}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 14 }}
              />
              <Tooltip content={<CallsTooltip />} />
              <Line
                type="monotone"
                dataKey="platformCalls"
                name={t('chart.legend.platform')}
                stroke={chartColors.platform}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="byokCalls"
                name={t('chart.legend.byok')}
                stroke={chartColors.byok}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="errorCalls"
                name={t('chart.legend.errors')}
                stroke={chartColors.errors}
                strokeWidth={2}
                strokeDasharray="6 6"
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </PageSectionCard>
  );
}

function CallsTooltip({ active, payload, label }: TooltipProps<number, string>) {
  const { t } = useT('dashboard');
  const point = payload?.[0]?.payload as AiKpisDailyPoint | undefined;

  if (!active || !point) return null;

  return (
    <div className="min-w-52 rounded-xl border border-border bg-background p-4 shadow-lg">
      <p className="text-sm font-medium">{formatDashboardDay(label ?? point.date)}</p>
      <p className="mt-1 text-sm text-muted-foreground">
        {t('chart.tooltip.total', { total: formatWholeNumber(point.callsTotal) })}
      </p>
      <ul className="mt-3 space-y-2 text-sm">
        {(payload ?? []).map((entry) => (
          <li key={entry.dataKey} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <span
                className="size-2.5 rounded-full"
                style={{ backgroundColor: entry.color }}
                aria-hidden="true"
              />
              <span>{entry.name}</span>
            </span>
            <span className="font-medium">{formatWholeNumber(Number(entry.value ?? 0))}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LegendChip({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="inline-flex min-h-11 items-center gap-3 rounded-full border border-border/70 bg-background px-4 py-2">
      <span
        className="size-2.5 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
      <span className="text-sm font-medium text-foreground">{label}</span>
      <span className="text-sm text-muted-foreground">{value}</span>
    </div>
  );
}
